"use client";

import { ArrowRightIcon, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import Image from "next/image";
import { useRef, useEffect, useState } from "react";
import { usePlayer } from "@/contexts/player-context";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface Video {
  ownerAvatarUrl: string;
  author: string;
  title: string;
  shareId: string;
  videoId: string;
  thumbnail: string;
  duration?: number;
  durationFormatted?: string;
  viewCount?: number;
  viewCountText?: string;
  publishedTimeText?: string;
  publishDate?: string;
  description?: string;
}

interface VideosSectionProps {
  videos: Video[];
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  title?: string;
}

function VideoItem({ video }: { video: Video }) {
  const { setCurrentEpisode } = usePlayer();
  const videoUrl = `/episodes/${video.videoId}`;

  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Set video in global player context
    const episodeData = {
      shareId: video.videoId, // Use videoId as shareId for video content
      content: {
        title: video.title,
        author: video.author,
        ownerAvatarUrl: video.ownerAvatarUrl,
        thumbnail: video.thumbnail,
        videoId: video.videoId,
        duration: video.duration,
      },
    };

    setCurrentEpisode(episodeData);
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="group cursor-pointer transition-all duration-200 hover:bg-primary/5 p-0 rounded-[8px]">
          <Link href={videoUrl}>
            <div className="space-y-3">
              {/* Video Thumbnail */}
              <div className="relative aspect-video overflow-hidden rounded-[8px] bg-muted shadow-md transition-all duration-300 group-hover:shadow-xl">
                <Image
                  src={
                    video.thumbnail ||
                    "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=320&h=180&fit=crop&crop=center"
                  }
                  alt={video.title}
                  fill
                  // className="object-cover"
                  loading="lazy"
                />

                {/* Duration badge */}
                {video.durationFormatted && (
                  <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded font-medium">
                    {video.durationFormatted}
                  </div>
                )}

                {/* Play overlay */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-all duration-300 group-hover:opacity-100">
                  <div className="transform scale-0 group-hover:scale-100 transition-transform duration-300">
                    <div className="rounded-full bg-black/60 p-3 text-white shadow-xl">
                      <Play className="h-5 w-5" fill="currentColor" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Video Info */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                  {video.title}
                </h3>
                
                <div className="flex items-center gap-2">
                  {/* Channel Avatar */}
                  <div className="flex-shrink-0 w-6 h-6 rounded-full overflow-hidden bg-primary/15">
                    <Image
                      src={
                        video.ownerAvatarUrl ||
                        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=24&h=24&fit=crop&crop=face"
                      }
                      alt={video.author}
                      width={24}
                      height={24}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>

                  {/* Channel Name */}
                  <span className="text-muted-foreground text-xs group-hover:text-muted-foreground/80 transition-colors duration-200 truncate">
                    {video.author}
                  </span>
                </div>

                {/* Video Stats */}
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {video.viewCountText && (
                    <>
                      <span>{video.viewCountText}</span>
                      {video.publishedTimeText && <span>•</span>}
                    </>
                  )}
                  {video.publishedTimeText && (
                    <span>{video.publishedTimeText}</span>
                  )}
                </div>
              </div>
            </div>
          </Link>
        </div>
      </TooltipTrigger>
      {video.description && (
        <TooltipContent side="top" className="max-w-xs text-sm">
          <p className="text-sm font-medium mb-1">{video.title}</p>
          <p className="text-xs text-muted-foreground line-clamp-3">
            {video.description.length > 150 
              ? `${video.description.substring(0, 150)}...`
              : video.description
            }
          </p>
        </TooltipContent>
      )}
    </Tooltip>
  );
}

export default function VideosSection({
  videos,
  isLoading,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  title = "Trending Videos",
}: VideosSectionProps) {
  const [isNearEnd, setIsNearEnd] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    // Disconnect existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoadingMore && onLoadMore) {
          onLoadMore();
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0.1,
      }
    );

    // Observe the trigger element
    const triggerElement = loadMoreTriggerRef.current;
    if (triggerElement && observerRef.current) {
      observerRef.current.observe(triggerElement);
    }

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoadingMore, onLoadMore]);

  // Loading skeleton
  if (isLoading || !videos || !Array.isArray(videos)) {
    return (
      <section>
        {/* Loading skeleton grid */}
        <div className="grid gap-3 sm:gap-3 md:gap-3 lg:gap-2 xl:gap-2 2xl:gap-2 grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
          {Array.from({ length: 16 }).map((_, index) => (
            <div key={index} className="space-y-3">
              <div className="aspect-video bg-muted animate-pulse rounded-[8px]"></div>
              <div className="space-y-2">
                <div className="bg-muted h-4 rounded animate-pulse"></div>
                <div className="bg-muted h-4 rounded w-3/4 animate-pulse"></div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-muted rounded-full animate-pulse"></div>
                  <div className="bg-muted h-3 rounded w-20 animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // No videos
  if (videos.length === 0) {
    return (
      <section>
        <div className="text-center py-8 text-muted-foreground">
          <p>No videos available at the moment.</p>
        </div>
      </section>
    );
  }

  return (
    <TooltipProvider>
      <section>
        {/* Videos Grid */}
        <div className="grid gap-3 sm:gap-3 md:gap-3 lg:gap-2 xl:gap-2 2xl:gap-2 grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
          {videos.map((video, index) => (
            <VideoItem
              key={`${video.videoId}-${index}`}
              video={video}
            />
          ))}
        </div>

        {/* Load More Section */}
        {videos.length > 0 && (
          <div className="pt-8">
            {/* Invisible trigger element for intersection observer */}
            {hasMore && !isLoadingMore && (
              <div
                ref={loadMoreTriggerRef}
                className="h-1"
                aria-hidden="true"
              />
            )}

            {/* Loading more indicator */}
            {isLoadingMore && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-muted-foreground">
                    Loading more videos...
                  </p>
                </div>
              </div>
            )}

            {/* End of results message */}
            {!hasMore && videos.length > 0 && (
              <div className="text-center py-12">
                <div className="max-w-md mx-auto">
                  <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="h-6 w-6 text-muted-foreground"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p className="text-muted-foreground">
                    You&apos;ve reached the end! That&apos;s all the
                    videos we found.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </TooltipProvider>
  );
}